import _ from 'lodash'
import React, { useMemo } from 'react'
import { useMedia } from 'react-media';
import { useDispatch, useSelector } from 'react-redux'
import { useQuery } from 'react-query'
import { Link, useParams, useHistory } from 'react-router-dom'
import { Popup, Button, Label, Dropdown, Icon, Menu, Image } from 'semantic-ui-react'
import SideNav, { Nav, NavItem, NavIcon, NavText } from '@trendmicro/react-sidenav';
import styled from 'styled-components';
import { usePersistedState } from '../hooks/use-persisted-state'
import { useFeatureFlag } from '../hooks/use-feature-flag'
import { fetchGetProducts, hasProduct } from '../queries/products'
import { fetchGetSystemStatus } from '../queries/system_status'
import { useWhatsNew } from '../apps/WhatsNewApp';
import permissionsSelectors from '../selectors/permissions'
import Products from '../constants/products'
import AppSettings from '../config'

import './EmployeeWrapper.scss'
import '@trendmicro/react-sidenav/dist/react-sidenav.css';

const NavWidth = 180
const NavWidthCollapsed = 60
const NavHeader = styled.div<{expanded:boolean}>`
    img {
      margin: 15px;
      margin-bottom: 25px;
      max-height: 40px
    }
`;
const CollapseButton = styled(Button)`
  position: absolute;
  top: 36px;
  transition: left .2s linear !important;
  left: ${(props:any) => (props.expanded ? NavWidth : NavWidthCollapsed) - 15}px;
`;
const StyledSideNav = styled(SideNav)`
  font-family: Lato,'Helvetica Neue',Arial,Helvetica,sans-serif;
  background-color: #323A52;
  min-width: ${(props:any) => props.expanded ? NavWidth : NavWidthCollapsed}px !important;
`;
StyledSideNav.defaultProps = SideNav.defaultProps;
const StyledNav = styled(Nav)`
  background-color: #323A52;
  display: flex;
  justify-content: flex-end;
  flex-direction: column;
  height: 100%;

  && > [class*="sidenav-navitem--"] {
      > [class*="navitem--"] {
          [class*="navicon--"] {
              &, > * {
                  color: white;
                  opacity: 1.0;
              }

              i {
                opacity: 0.7
              }

              [class*="navchip--"] {
                width: 5px;
                height: 100%;
                position: absolute;
                left: 0;
              }

          }
          [class*="navtext--"] {
              &, > * {
                  color: white;
                  font-size: 1.0em;
              }
          }
      }
  }
  && > [class*="sidenav-navitem--"]:hover {
      > [class*="navitem--"] {
          background-color: rgba(1,1,1,0.1);

          i {
            opacity: 1.0;
          }
      }
  }
`;
StyledNav.defaultProps = Nav.defaultProps;
const StyledBottomNav = styled(StyledNav)`
  margin: '0px',
  marginTop: 'auto'
`;
const SquaredMenu = styled(Menu)`
  border-radius: 0 !important;
`;
const Main = styled.main<{expanded:boolean}>`
    position: relative;
    overflow-y: auto;
    padding: 0 0;
    height: 100vh;
`;
const MobileMenuItem = ({item}:any) => {
  if(item.options) {
    return (
      <>
        {item.options.map((subitem:any) => (
          <Menu.Item key={subitem.text} as={subitem.href !== undefined ? 'a' : Link} href={subitem.href} to={subitem.to}>
            <Icon name={subitem.icon}/>{subitem.text}
            {subitem.badge}
          </Menu.Item>
        ))}
      </>
    )
  }
  return (
    <Menu.Item as={item.href !== undefined ? 'a' : Link} href={item.href} to={item.to}>
      <Icon name={item.icon}/>{item.text}
      {item.badge}
    </Menu.Item>
  )
}

const DesktopMenuItem = ({item, expanded}:any) => {
  const history = useHistory();
  const selected = item?.isSelected && item.isSelected()
  const onClick = (item:any) => () => {
    if(item.to) {
      history.push(item.to)
    } else if(item.href){
      window.location.href = item.href;
    }
  }
  if(item.options) {
    return (
      <Popup
        key={item.text}
        hoverable
        position='right center'
        trigger={(
          <NavItem>
            <NavIcon>
              <div style={{position: 'relative'}}>
                <Icon name={item.icon} size='big'/>
                {item.badge}
              </div>
            </NavIcon>
            <NavText>
              {item.text}
            </NavText>
          </NavItem>
        )}
      >
        <Menu secondary vertical>
          {item.options.map((subitem:any) => (
            <Menu.Item key={subitem.text} onClick={onClick(subitem)}>{subitem.text}</Menu.Item>
          ))}
        </Menu>
      </Popup>
    )
  }
  return (
    <Popup
      inverted
      disabled={expanded}
      content={item.text}
      position='right center'
      trigger={(
        <NavItem onClick={onClick(item)}>
          <NavIcon>
            <div className="sidenav---navchip---" style={{backgroundColor: selected ? 'rgb(249, 216, 96)' : 'transparent'}}></div>
            <div style={{position: 'relative'}}>
              <Icon name={item.icon} size='big'/>
              {item.badge}
            </div>
          </NavIcon>
          <NavText>
            {item.text}
          </NavText>
        </NavItem>
      )}
    />
  );
}

const MenuItems = ({ mobile, expanded }: {mobile?:boolean, expanded?: boolean}) => {
  const params = useParams<{site:string}>();
  const dispatch = useDispatch();
  const whatsNew = useWhatsNew();
  const productsQuery = useQuery(['products', params.site], fetchGetProducts(dispatch));
  const hasCrm = hasProduct(productsQuery, Products.CRM)
  const hasCe = hasProduct(productsQuery, Products.CE)
  const hasCommunications = hasProduct(productsQuery, Products.COMMUNICATIONS);
  const hasPermits = hasProduct(productsQuery, Products.PERMITS);

  const featureCode = useFeatureFlag('ce.new_look_list');
  const featureCrm = useFeatureFlag('crm.new_look_list');
  const canManageCommunications = useSelector(permissionsSelectors.can('manage_communications'));
  const canReviewPermits = useSelector(permissionsSelectors.can('review_permits'));

  const systemStatusQuery = useQuery(['systemStatus', { per_page: 1, sort: 'id', order: 'desc' }], fetchGetSystemStatus(), {
      refetchInterval: 5000
  })
  const systemStatusColor = useMemo(() =>
    systemStatusQuery.data ? _.get(systemStatusQuery, 'data.data[0].status') === 4 ? 'green' : 'yellow' : 'grey'
  , [systemStatusQuery.data])

  const structure = {
    top: [{
      show: hasCrm,
      icon: 'id card outline',
      text: 'CRM',
      href: `${AppSettings.GOGOV_WEB_URL}/${params.site}/internal.php`,
      isSelected: () => window.location.pathname.includes('/crm')
    },{
      show: hasCe,
      icon: 'briefcase',
      text: 'Code',
      to: featureCode.isEnabled ? `/${params.site}/code` : undefined,
      href: !featureCode.isEnabled ? `${AppSettings.GOGOV_WEB_URL}/${params.site}/internal.php` : undefined,
      isSelected: () => window.location.pathname.includes('/code')
     },{
      show: true,
      icon: 'chart bar outline',
      text: 'Reporting',
      href: `${AppSettings.GOGOV_WEB_URL}/${params.site}/reportlist.php`
    },{
      show: hasCommunications && canManageCommunications,
      icon: 'bullhorn',
      text: 'Communications',
      to: `/${params.site}/communications`,
      isSelected: () => window.location.pathname.includes('/communications')
    },{
      show: hasPermits && canReviewPermits,
      icon: 'clipboard check',
      text: 'Permits',
      to: `/${params.site}/permits`,
      isSelected: () => window.location.pathname.includes('/permits')
    }],
    bottom: [{
      show: true,
      icon: 'lightning',
      text: 'What\'s New',
      badge: whatsNew.shouldShowBadge && (
        <Label style={{top: '0px', left: '50px'}} floating size='mini' color="teal">{whatsNew.badge}</Label>
      ),
      to: `/${params.site}/whats-new`,
      isSelected: () => window.location.pathname.includes('/whats-new')
    },{
      show: true,
      icon: 'tasks',
      text: 'Admin',
      href: `${AppSettings.GOGOV_WEB_URL}/${params.site}/admin/index.php`
    },{
      show: true,
      icon: 'user outline',
      text: 'Account',
      options: [{
        show: true,
        icon: 'user outline',
        text: 'Profile',
        href: `${AppSettings.GOGOV_WEB_URL}/${params.site}/profile.php?cmd=edit`,
      },{
        show: true,
        icon: 'log out',
        text: 'Logout',
        href: `${AppSettings.GOGOV_WEB_URL}/${params.site}/logout.php`
      }]
    },{
      show: true,
      icon: 'computer',
      text: 'System Status',
      href: AppSettings.SYSTEM_STATUS_URL,
      badge: <Label style={{top: '10px', left: '55px'}} color={systemStatusColor} floating circular empty></Label>
    }
    ]
  }

  if(mobile) {
    return (
      <>
        <div className='top-aligned'>
          {
            structure
            .top
            .filter(item => item.show)
            .map(item => <MobileMenuItem key={item.text} item={item}/>)
          }
        </div>
        <div className='bottom-aligned'>
          {
            structure
            .bottom
            .filter(item => item.show)
            .map(item => <MobileMenuItem key={item.text} item={item}/>)
          }
        </div>
      </>
    )
  }

  return (
    <>
      {
        structure
        .top
        .filter(item => item.show)
        .map(item => <DesktopMenuItem key={item.text} item={item} expanded={expanded}/>)
      }
      <StyledBottomNav>
      {
        structure
        .bottom
        .filter(item => item.show)
        .map(item => <DesktopMenuItem key={item.text} item={item} expanded={expanded}/>)
      }
      </StyledBottomNav>
    </>
  )
}

export default (props:any) => {
  const isSmallScreen = useMedia({ query: "(max-width: 640px)" });
  const [expanded, setExpanded] = usePersistedState('menu.expanded', true);
  const child = useMemo(() => props.render(), [props]);
  const marginLeft = expanded ? NavWidth : NavWidthCollapsed;

  return (
    <>
      {isSmallScreen && (
        <div className="EmployeeWrapper--mobile hidden-print">
          <SquaredMenu inverted>
            <Menu.Item>
              <Image src={`${AppSettings.ASSETS_URL}/branding/GOGov_logo_only_fullcolor.svg`}/>
            </Menu.Item>
            <Menu.Menu position="right">
              <Dropdown item icon='bars'>
                <Dropdown.Menu>
                  <MenuItems mobile={true}/>
                </Dropdown.Menu>
              </Dropdown>
            </Menu.Menu>
          </SquaredMenu>
        </div>
      )}
      {!isSmallScreen && (
        <StyledSideNav expanded={expanded} className="hidden-print">
          <StyledNav>
            <CollapseButton circular 
              expanded={expanded}
              onClick={() => setExpanded(!expanded)}
              size='mini' icon={expanded ? 'chevron left' : 'chevron right'}/>
            <NavHeader expanded={expanded}>
              {!expanded && <Image src={`${AppSettings.ASSETS_URL}/branding/GOGov_logo_only_fullcolor.svg`}/>}
              {expanded && <Image src={`${AppSettings.ASSETS_URL}/branding/Full%20Logo%20Color%20%2B%20White%20TextVector.svg`}/>}
            </NavHeader>
            <MenuItems expanded={expanded}/>
          </StyledNav>
        </StyledSideNav>
      )}
      <Main style={{ marginLeft: isSmallScreen ? 0 : marginLeft }}>
        {child}
      </Main>
    </>
  )
}
